import { useState, useEffect, useMemo, useRef } from 'react';
import { useDeckStore, getPersistedCardNames, getPersistedEntries, getPersistedSideboardEntries } from '../../stores/deck-store.js';
import { useCollectionStore } from '../../stores/collection-store.js';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { api } from '../../lib/api-client.js';
import { DeckImportModal } from './DeckImportModal.js';
import { CardPreviewArea } from './CardPreviewArea.js';
import { TagProfile } from './TagProfile.js';
import { SideboardTab } from './SideboardTab.js';
import BracketTab from './BracketTab.js';
import { RecommendationsTab } from './RecommendationsTab.js';
import { useValidation } from '../../hooks/useValidation.js';
import { useBracket } from '../../hooks/useBracket.js';
import { useRecommendations } from '../../hooks/useRecommendations.js';
import { ManaSymbolIcons } from '../shared/ManaSymbolIcons.js';
import { formatDeckAsFile, sanitiseDeckName } from '../../lib/deck-export.js';
import { ValidationBanner } from './ValidationBanner.js';

type DeckTab = 'discover' | 'tags' | 'sideboard' | 'bracket';

export function DeckZone() {
  const [showImport, setShowImport] = useState(false);
  const [activeTab, setActiveTab] = useState<DeckTab>('tags');
  const [showNoSaveMessage, setShowNoSaveMessage] = useState(false);
  const [showSaveAndPlaytest, setShowSaveAndPlaytest] = useState(false);

  const initializeGame = usePlaytesterStore((s) => s.initializeGame);
  const entries = useDeckStore((s) => s.entries);
  const sideboard = useDeckStore((s) => s.sideboard);
  const deckName = useDeckStore((s) => s.deckName);
  const setDeckName = useDeckStore((s) => s.setDeckName);
  const deckId = useDeckStore((s) => s.deckId);
  const importEntries = useDeckStore((s) => s.importEntries);
  const clearDeck = useDeckStore((s) => s.clearDeck);
  const colorIdentity = useDeckStore((s) => s.colorIdentity);
  const saveDeck = useCollectionStore((s) => s.saveDeck);

  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'local'>('saved');
  const lastSavedStateRef = useRef<string>('');

  const [isEditing, setIsEditing] = useState(false);
  const [tempName, setTempName] = useState(deckName);

  useEffect(() => {
    if (!isEditing) {
      setTempName(deckName);
    }
  }, [deckName, isEditing]);

  const validation = useValidation();
  // Auto-evaluate bracket on deck changes (result cached in store)
  useBracket();
  // Auto-fetch recommendations (always mounted, not tab-dependent)
  const { isLoading: recsLoading } = useRecommendations();

  // Clear import loading overlay when ALL async data is ready
  const isImportLoading = useDeckStore((s) => s.isImportLoading);
  const setImportLoading = useDeckStore((s) => s.setImportLoading);
  const isProfileLoading = useDeckStore((s) => s.isProfileLoading);
  const isDirectionsLoading = useDeckStore((s) => s.isDirectionsLoading);
  const moxfieldDeckId = useDeckStore((s) => s.moxfieldDeckId);
  const recommendationsCache = useDeckStore((s) => s.recommendationsCache);

  // Synchronise saveStatus and track last saved state on deckId changes (switching decks)
  useEffect(() => {
    if (deckId) {
      const stateStr = JSON.stringify({
        deckName,
        moxfieldDeckId,
        colorIdentity,
        entries: entries.map(e => ({ id: e.card.id, q: e.quantity, cmd: e.isCommander })),
        sideboard: sideboard.map(s => s.card.id),
      });
      lastSavedStateRef.current = stateStr;
      setSaveStatus('saved');
    } else {
      setSaveStatus('local');
    }
  }, [deckId]);

  // Debounced auto-saving on deck state updates
  useEffect(() => {
    if (!deckId) {
      setSaveStatus('local');
      return;
    }

    const stateStr = JSON.stringify({
      deckName,
      moxfieldDeckId,
      colorIdentity,
      entries: entries.map(e => ({ id: e.card.id, q: e.quantity, cmd: e.isCommander })),
      sideboard: sideboard.map(s => s.card.id),
    });

    if (stateStr === lastSavedStateRef.current) {
      setSaveStatus('saved');
      return;
    }

    setSaveStatus('saving');

    const timeoutId = setTimeout(() => {
      const commandersList = entries.filter((e) => e.isCommander);
      const commanderNames = commandersList.map((e) => e.card.name);
      const cardCount = entries.reduce((sum, e) => sum + e.quantity, 0);

      saveDeck({
        id: deckId,
        moxfieldDeckId: moxfieldDeckId,
        name: deckName,
        colorIdentity: colorIdentity,
        commanderNames: commanderNames,
        cardCount: cardCount,
        entries: entries,
        sideboard: sideboard,
      })
      .then(() => {
        lastSavedStateRef.current = stateStr;
        setSaveStatus('saved');
      })
      .catch((err) => {
        console.error('[auto-save-error]', err);
        setSaveStatus('local');
      });
    }, 1200);

    return () => clearTimeout(timeoutId);
  }, [deckId, deckName, entries, sideboard, colorIdentity, moxfieldDeckId, saveDeck]);

  useEffect(() => {
    if (!isImportLoading) return;
    const recsReady = !moxfieldDeckId || recommendationsCache.has(moxfieldDeckId);
    const profileReady = !isProfileLoading;
    const directionsReady = !isDirectionsLoading;
    if (recsReady && profileReady && directionsReady) {
      setImportLoading(false);
    }
  }, [isImportLoading, recsLoading, isProfileLoading, isDirectionsLoading, moxfieldDeckId, recommendationsCache, setImportLoading]);
  // Auto-load tag profile when deck has cards but no profile yet
  const importGeneration = useDeckStore((s) => s.importGeneration);
  useEffect(() => {
    const { tagProfile, isProfileLoading, entries: currentEntries, setProfileLoading, setTagProfile } = useDeckStore.getState();
    if (currentEntries.length > 0 && tagProfile === null && !isProfileLoading) {
      const names = currentEntries.map((e) => e.card.name);
      setProfileLoading(true);
      api.profileDeck(names, false)
        .then((response) => {
          setTagProfile(response.profile, response.hierarchy);
        })
        .catch((err) => {
          console.error('[auto-tag-profile]', err);
        })
        .finally(() => {
          setProfileLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length, importGeneration]);

  const commanders = entries.filter((e) => e.isCommander);

  // Rehydrate persisted deck on mount
  useEffect(() => {
    const cardNames = getPersistedCardNames();
    if (cardNames.length === 0 || entries.length > 0) return;

    const persisted = getPersistedEntries();
    const persistedSideboard = getPersistedSideboardEntries();
    api
      .resolveCards(cardNames)
      .then(({ found }) => {
        const resolvedMap = new Map<string, typeof found[number]>();
        for (const c of found) {
          resolvedMap.set(c.name.toLowerCase(), c);
          if (c.name.includes(' // ')) resolvedMap.set(c.name.split(' // ')[0]!.toLowerCase(), c);
        }
        const rehydrated = persisted
          .map((p) => {
            const card = resolvedMap.get(p.cardName.toLowerCase());
            if (!card) return null;
            return { card, quantity: p.quantity, isCommander: p.isCommander };
          })
          .filter((e): e is NonNullable<typeof e> => e !== null);
        if (rehydrated.length > 0) importEntries(rehydrated);

        // Rehydrate sideboard cards
        const { addToSideboard } = useDeckStore.getState();
        for (const ps of persistedSideboard) {
          const card = resolvedMap.get(ps.cardName.toLowerCase());
          if (card) addToSideboard(card);
        }
      })
      .catch(() => {
        // Server may not be running yet — silently skip rehydration
      });
    // Run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalCards = entries.reduce((sum, e) => sum + e.quantity, 0);
  const hasCards = entries.length > 0;

  const totalPriceEur = useMemo(() => {
    if (entries.length === 0) return null;
    let total = 0;
    let hasAnyPrice = false;
    for (const entry of entries) {
      const eur = entry.card.prices.eur;
      if (eur != null) {
        total += parseFloat(eur) * entry.quantity;
        hasAnyPrice = true;
      }
    }
    return hasAnyPrice ? total.toFixed(2) : null;
  }, [entries]);

  function handlePlaytest(): void {
    if (!deckId) {
      setShowNoSaveMessage(true);
      setTimeout(() => setShowNoSaveMessage(false), 3000);
      return;
    }
    if (saveStatus === 'saving') return;
    if (saveStatus === 'local') {
      // Unsaved changes — prompt the user to save first (AC-243)
      setShowSaveAndPlaytest(true);
      return;
    }
    const { entries: currentEntries, deckId: currentDeckId, deckName: currentDeckName } = useDeckStore.getState();
    initializeGame(
      { id: currentDeckId ?? '', name: currentDeckName, entries: currentEntries },
      'editor',
    );
  }

  async function handleSaveAndPlaytest(): Promise<void> {
    setShowSaveAndPlaytest(false);
    const { entries: currentEntries, deckId: currentDeckId, deckName: currentDeckName } = useDeckStore.getState();
    // Save synchronously before launching playtester
    try {
      await saveDeck({
        id: deckId!,
        moxfieldDeckId: null,
        name: currentDeckName,
        colorIdentity,
        commanderNames: currentEntries.filter((e) => e.isCommander).map((e) => e.card.name),
        cardCount: currentEntries.reduce((sum, e) => sum + e.quantity, 0),
        entries: currentEntries,
        sideboard: [],
      });
    } catch (err) {
      console.error('[save-and-playtest]', err);
    }
    initializeGame(
      { id: currentDeckId ?? '', name: currentDeckName, entries: currentEntries },
      'editor',
    );
  }

  function handleExport(): void {
    try {
      const content = formatDeckAsFile(entries, sideboard);
      const filename = `${sanitiseDeckName(deckName)}.txt`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[deck-export]', err);
    }
  }

  const tabClass = (tab: DeckTab) =>
    `flex-1 py-2 text-xs font-medium text-center transition-colors ${
      activeTab === tab
        ? 'text-orange-400 border-b-2 border-orange-500'
        : 'text-gray-500 hover:text-gray-300 border-b-2 border-transparent'
    }`;

  return (
    <div className="flex flex-col h-full border-r border-gray-800 bg-gray-950 w-full lg:w-[400px] xl:w-[440px] shrink-0">
      {/* ── Always-visible header ────────────────────────────────── */}
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
        <div>
          <h1 className="text-sm font-semibold text-gray-100 flex items-center gap-2">
            {isEditing ? (
              <input
                type="text"
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onBlur={() => {
                  setIsEditing(false);
                  const trimmed = tempName.trim();
                  if (trimmed && trimmed !== deckName) {
                    setDeckName(trimmed);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  } else if (e.key === 'Escape') {
                    setTempName(deckName);
                    setIsEditing(false);
                  }
                }}
                autoFocus
                className="bg-gray-800 text-gray-100 border border-gray-700 px-2 py-0.5 rounded text-sm font-semibold focus:outline-none focus:border-orange-500 w-48"
              />
            ) : (
              <span
                onClick={() => setIsEditing(true)}
                className="cursor-pointer hover:text-orange-400 flex items-center gap-1.5 transition-colors group"
                title="Click to rename deck"
              >
                {deckName}
                <svg className="w-3.5 h-3.5 text-gray-500 group-hover:text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-[10px] text-green-500 font-medium flex items-center gap-1 shrink-0" title="All changes saved to database">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Saved
              </span>
            )}
            {saveStatus === 'saving' && (
              <span className="text-[10px] text-orange-400 font-medium flex items-center gap-1 shrink-0 animate-pulse" title="Saving changes...">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                Saving...
              </span>
            )}
            {saveStatus === 'local' && (
              <span className="text-[10px] text-gray-500 font-medium flex items-center gap-1 shrink-0" title="Changes are local to this session only">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                Local Session
              </span>
            )}
          </h1>
          <p className="text-xs mt-0.5 flex items-center gap-1">
            <span className={totalCards > 100 ? 'text-red-400' : totalCards === 100 ? 'text-green-500' : 'text-gray-600'}>
              {totalCards === 0 ? 'Empty deck' : `${totalCards}/100`}
            </span>
            {totalPriceEur !== null && (
              <span className="text-gray-400">| €{totalPriceEur}</span>
            )}
            {validation.violations.length > 0 && (
              <span className="text-yellow-500" title={validation.violations.map((v) => v.message).join('\n')}>
                ⚠ {validation.violations.length}
              </span>
            )}
          </p>
          {commanders.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-orange-500 text-[10px] font-bold">&#x2694;</span>
              <span className="text-xs text-gray-300 truncate">
                {commanders.map((e) => e.card.name).join(' & ')}
              </span>
              <ManaSymbolIcons colors={colorIdentity} size={14} />
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            {hasCards && (
              <button
                onClick={clearDeck}
                className="text-xs text-gray-600 hover:text-red-400 transition-colors"
              >
                Clear
              </button>
            )}
            {hasCards && (
              <button
                onClick={handleExport}
                className="px-3 py-1.5 text-xs font-medium border border-gray-600 hover:border-gray-400 text-gray-400 hover:text-gray-200 rounded-lg transition-colors"
              >
                Export
              </button>
            )}
            {hasCards && (
              <button
                onClick={handlePlaytest}
                disabled={saveStatus === 'saving'}
                className="px-3 py-1.5 text-xs font-medium bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border border-orange-600/30 hover:border-orange-500/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={saveStatus === 'local' ? 'Unsaved changes — will prompt to save first' : 'Playtest this deck'}
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Playtest'}
              </button>
            )}
            <button
              onClick={() => setShowImport(true)}
              className="px-3 py-1.5 text-xs font-medium bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors"
            >
              Import
            </button>
          </div>
          {showNoSaveMessage && (
            <p className="text-[10px] text-amber-400 font-medium">
              Save your deck to the collection first
            </p>
          )}
          {showSaveAndPlaytest && (
            <div className="text-[10px] text-amber-300 font-medium flex items-center gap-2 flex-wrap">
              <span>Unsaved changes.</span>
              <button
                onClick={() => void handleSaveAndPlaytest()}
                className="underline hover:text-white transition"
              >
                Save &amp; Playtest
              </button>
              <button
                onClick={() => setShowSaveAndPlaytest(false)}
                className="text-gray-500 hover:text-white transition"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <ValidationBanner violations={validation.violations} />

      <CardPreviewArea />

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      {(hasCards || sideboard.length > 0) && (
        <div className="flex border-b border-gray-800 shrink-0">
          <button className={tabClass('tags')} onClick={() => setActiveTab('tags')}>
            Tag Profile
          </button>
          <button className={tabClass('discover')} onClick={() => setActiveTab('discover')}>
            Discover
          </button>
          <button className={tabClass('sideboard')} onClick={() => setActiveTab('sideboard')}>
            Sideboard{sideboard.length > 0 ? ` (${sideboard.length})` : ''}
          </button>
          <button className={tabClass('bracket')} onClick={() => setActiveTab('bracket')}>
            Bracket
          </button>
        </div>
      )}

      {/* ── Tab content ──────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
        {activeTab === 'sideboard' ? (
          <SideboardTab />
        ) : activeTab === 'bracket' ? (
          <BracketTab />
        ) : !hasCards ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-4 text-center px-6 py-12">
            <div className="w-16 h-16 rounded-full bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400 text-2xl animate-pulse">
              ⚔
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-200">Designate a Commander</h3>
              <p className="text-xs text-gray-500 mt-1.5 max-w-xs leading-relaxed">
                Commander decks require a leader to define their color identity. Search for a card on the right and click <span className="text-orange-400 font-medium">⚔ (Add as Commander)</span> to begin.
              </p>
            </div>
            <div className="flex items-center gap-3 w-full max-w-xs mt-2">
              <div className="h-px bg-gray-800 flex-1" />
              <span className="text-[10px] text-gray-600 uppercase font-bold tracking-wider">or</span>
              <div className="h-px bg-gray-800 flex-1" />
            </div>
            <button
              onClick={() => setShowImport(true)}
              className="px-4 py-2 text-xs font-semibold bg-gray-900 border border-gray-800 hover:border-gray-700 text-gray-300 hover:text-white rounded-xl transition-all shadow-md w-full max-w-xs"
            >
              Import Deck List
            </button>
          </div>
        ) : activeTab === 'discover' ? (
          <RecommendationsTab />
        ) : (
          <TagProfile />
        )}
      </div>

      {showImport && <DeckImportModal onClose={() => setShowImport(false)} />}
    </div>
  );
}
